import React, { useState, useEffect, useCallback, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Plus,
  Search,
  Settings2,
  Trash2,
  History,
  RotateCcw,
  XCircle,
  Clock,
  MapPin,
  Globe,
  ShieldCheck,
  Tag,
  Zap,
  CheckCircle2,
  MinusCircle,
  Pencil,
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../loader/Loader";
import AddPersonalizedItems from "./AddPersonalizedItems";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";

const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "—");

// ──────────────────────────────────────────────────────────
// Price-type pill — shown on each row
// ──────────────────────────────────────────────────────────
const PT_META = [
  { key: "showVatInc", label: "VAT Inc", color: "emerald" },
  { key: "showVatEx", label: "VAT Ex", color: "blue" },
  { key: "showReseller", label: "Reseller", color: "violet" },
  { key: "showZeroRated", label: "0-Rated", color: "amber" },
];

const PILL_ACTIVE = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  blue: "bg-blue-100    text-blue-700    border-blue-200",
  violet: "bg-violet-100  text-violet-700  border-violet-200",
  amber: "bg-amber-100   text-amber-700   border-amber-200",
};

const PriceTypePills = ({ item }) => (
  <div className="flex flex-wrap gap-1.5">
    {PT_META.map((pt) =>
      item[pt.key] ? (
        <span
          key={pt.key}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${PILL_ACTIVE[pt.color]}`}
        >
          <CheckCircle2 size={10} />
          {pt.label}
        </span>
      ) : (
        <span
          key={pt.key}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 text-[10px] font-medium text-slate-400 bg-slate-50"
        >
          <MinusCircle size={10} />
          {pt.label}
        </span>
      ),
    )}
  </div>
);

// ──────────────────────────────────────────────────────────
// DELETED HISTORY MODAL
// ──────────────────────────────────────────────────────────
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebounced] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${domain}/api/PersonalizedPricing/history?search=${debouncedSearch}`,
      );
      setHistoryData(res.data);
    } catch {
      toast.error("Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setDebounced("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = historyData.slice(
    indexOfLast - itemsPerPage,
    indexOfLast,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Pricing Configurations
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by config name or location..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted configurations found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Desktop table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Config Name</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Price Types</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1.5">
                          <Settings2 size={13} className="text-indigo-400" />
                          {item.configName}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                            title="View Audit Info"
                          >
                            <Clock size={13} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {item.locationName ?? (
                            <span className="text-slate-400 italic">
                              Global
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <PriceTypePills item={item} />
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-800">
                          {item.configName}
                        </h4>
                        <span className="text-xs text-slate-400">
                          {item.locationName ?? "Global"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100 transition"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <PriceTypePills item={item} />
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                      <Trash2 size={10} className="text-red-400" />
                      Deleted by:{" "}
                      <span className="font-medium text-red-500 ml-0.5">
                        {item.deletedByFullName || "Unknown"}
                      </span>
                      <span className="ml-auto text-red-400">
                        {formatDate(item.deletedDate)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.configName} — Audit`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────
const AllPersonalizedItems = () => {
  const currentUserID = useSelector(selectUserID);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryModal, setIsHistoryModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // ── Reset page on search ──
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${domain}/api/PersonalizedPricing`);
      setData(res.data);
    } catch {
      toast.error("Failed to fetch pricing configurations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Filter ──
  const filtered = data.filter(
    (item) =>
      (item.configName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (item.locationName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = filtered.slice(indexOfLast - itemsPerPage, indexOfLast);

  // ── Soft Delete ──
  const handleSoftDelete = async (id) => {
    if (!window.confirm("Delete this configuration?")) return;
    try {
      await axios.put(
        `${domain}/api/PersonalizedPricing/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Configuration moved to history.");
      fetchData();
    } catch {
      toast.error("Failed to delete configuration.");
    }
  };

  // ── Restore ──
  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/PersonalizedPricing/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Configuration restored!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to restore.");
    }
  };

  // ── Open modal helpers ──
  const openAdd = () => {
    setItemToEdit(null);
    setIsModalVisible(true);
  };
  const openEdit = (item) => {
    setItemToEdit({ ...item });
    setIsModalVisible(true);
  };

  // ── Active count badge per row ──
  const countActive = (item) => PT_META.filter((pt) => item[pt.key]).length;

  return (
    <div className="min-h-screen bg-slate-100/40 pb-12 font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-white to-slate-50/50 border-b border-slate-100 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2.5 tracking-tight">
                <div className="p-2.5 rounded-lg bg-indigo-100/60 text-indigo-600">
                  <Settings2 size={20} />
                </div>
                Pricing Display Configuration
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                Control which price columns (VAT Inc, VAT Ex, Reseller, Zero
                Rated) are active per location or globally.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModal(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 text-sm font-medium px-4 py-2.5 rounded-lg transition-all duration-200"
              >
                <History size={17} />
                <span>History</span>
              </button>
              <button
                onClick={openAdd}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all active:scale-95 duration-200"
              >
                <Plus size={18} /> New Config
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* ── Search bar ── */}
        <div className="bg-white border border-slate-100 p-4 rounded-xl shadow-sm mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by config name or location..."
              className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 text-sm text-slate-700 outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="flex flex-wrap gap-3 mb-5">
          {PT_META.map((pt) => (
            <span
              key={pt.key}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${PILL_ACTIVE[pt.color]}`}
            >
              <CheckCircle2 size={11} />
              {pt.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-200 text-slate-400 bg-slate-50 text-xs font-semibold">
            <MinusCircle size={11} />
            Inactive
          </span>
        </div>

        {/* ── Table ── */}
        {loading ? (
          <Loader />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 text-xs uppercase text-slate-500 font-semibold">
                <tr>
                  <th className="px-6 py-4 font-semibold tracking-wide">
                    Config Name
                  </th>
                  <th className="px-6 py-4 font-semibold tracking-wide">
                    Location Scope
                  </th>
                  <th className="px-6 py-4 font-semibold tracking-wide">
                    Active Price Types
                  </th>
                  <th className="px-6 py-4 font-semibold tracking-wide text-center">
                    Active
                  </th>
                  <th className="px-6 py-4 font-semibold tracking-wide text-center">
                    Created
                  </th>
                  <th className="px-6 py-4 font-semibold tracking-wide text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-indigo-50/20 transition-colors duration-150"
                    >
                      {/* Config Name */}
                      <td className="px-6 py-4 align-middle">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-500">
                            <Settings2 size={15} />
                          </div>
                          <div>
                            <span className="font-semibold text-slate-800 text-sm block">
                              {item.configName}
                            </span>
                            <span className="text-xs text-slate-400">
                              ID #{item.id}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-6 py-4 align-middle">
                        {item.locationName ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">
                            <MapPin size={11} />
                            {item.locationName}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">
                            <Globe size={11} />
                            Global
                          </span>
                        )}
                      </td>

                      {/* Price type pills */}
                      <td className="px-6 py-4 align-middle">
                        <PriceTypePills item={item} />
                      </td>

                      {/* Count badge */}
                      <td className="px-6 py-4 align-middle text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">
                          {countActive(item)}
                        </span>
                      </td>

                      {/* Created date */}
                      <td className="px-6 py-4 align-middle text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-xs text-slate-500">
                            {formatDate(item.createdDate)}
                          </span>
                          {item.createdByFullName && (
                            <span className="text-[10px] text-slate-400">
                              {item.createdByFullName}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 align-middle">
                        <div className="flex items-center justify-end gap-2">
                          {/* Audit */}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="p-2 rounded-lg bg-amber-50 text-amber-500 hover:bg-amber-100 transition-colors"
                            title="Audit Info"
                          >
                            <Clock size={15} />
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => openEdit(item)}
                            className="p-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => handleSoftDelete(item.id)}
                            className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-14">
                      <div className="inline-flex flex-col items-center gap-2 text-slate-400">
                        <Settings2 size={28} className="opacity-30" />
                        <span className="text-sm">
                          No configurations found.
                        </span>
                        <button
                          onClick={openAdd}
                          className="mt-2 text-indigo-600 text-xs font-semibold hover:underline"
                        >
                          + Create your first config
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="px-6 py-4 border-t border-slate-100 bg-gradient-to-r from-slate-50/50 to-white">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filtered.length}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <AddPersonalizedItems
              onClose={() => {
                setIsModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              itemToEdit={itemToEdit}
            />
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      <DeletedHistoryModal
        isOpen={isHistoryModal}
        onClose={() => {
          setIsHistoryModal(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* ── Audit Modal ── */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.configName} — Audit`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllPersonalizedItems;
